import { Modal } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { uniqueId } from "lodash-es";
import React from "react";
import { FormikObserveState } from "../../commons/FormikStateObserver.js";
import { useHasSapS4HanaIntegration } from "../sap-s4-hana/hooks.js";
import { Sap4HanaCrmCreateForm } from "../sap-s4-hana/S4HanaCrmCreateForm.js";
import { CrmCreateForm } from "./CrmCreateForm.js";
import { CreateCrmCompany_CrmCompanyFragment } from "../crm-companies/CrmCompany.generated.js";

interface Props
  extends
    Omit<
      React.ComponentProps<typeof CrmCreateForm>,
      "handleFormikObserveStateChange" | "formId" | "handleComplete"
    >,
    Omit<
      React.ComponentProps<typeof Sap4HanaCrmCreateForm>,
      "handleFormikObserveStateChange" | "formId" | "handleComplete"
    > {
  id?: string;
  title?: string;
  handleClose: () => void;
  handleComplete: (
    handleClose: () => void,
    organisation: CreateCrmCompany_CrmCompanyFragment,
    userId?: string
  ) => void;
}

export const CrmCreateModal = ({
  id,
  title,
  handleClose,
  handleComplete,
  ...props
}: Props) => {
  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();
  const { t } = useTranslate(["Global", "CrmOrganisations", "SapS4Hana"]);

  const formId = React.useMemo(() => uniqueId(), []);
  const [formikState, setFormikState] =
    React.useState<FormikObserveState | null>(null);

  return (
    <Modal
      id={id}
      title={title ?? t("Create client", { ns: "CrmOrganisations" })}
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: {
            variant: "text",
            disabled: formikState?.isSubmitting,
          },
        },
        {
          label: t("Create", { ns: "Global" }),
          buttonProps: {
            type: "submit",
            form: formId,
            loading: formikState?.isSubmitting || formikState?.isValidating,
            // IMPORTANT! We cannot have `!isValid` check here because validation
            // on `change` and `blur` form field events is off in Formik
            // due to an email validation - we want to have it only once on submit
            // disabled: !formikState?.isValid,
          },
        },
      ]}
    >
      {hasSapS4HanaIntegration ? (
        <Sap4HanaCrmCreateForm
          {...props}
          formId={formId}
          handleFormikObserveStateChange={setFormikState}
          handleClose={handleClose}
          handleComplete={handleComplete}
        />
      ) : (
        <CrmCreateForm
          {...props}
          formId={formId}
          handleFormikObserveStateChange={setFormikState}
          handleComplete={(organisation, userId) =>
            handleComplete?.(handleClose, organisation, userId)
          }
        />
      )}
    </Modal>
  );
};
